import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useAuth } from '@/modules/auth/presentation/context/AuthContext';
import { BackButton } from '@/presentation/components/BackButton';

export function AdminDashboardPage() {
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const auth = useAuth();
  const { t } = useTranslation('common');

  const OPTS = [
    {
      id: 'empleados',
      label: t('admin.employeesLabel'),
      sub: t('admin.employeesSub'),
      color: '#8FDDB1',
      fg: '#1B6B3F',
      path: '/admin/empleados',
      icon: (
        <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
    {
      id: 'marcajes',
      label: t('admin.timelogsLabel'),
      sub: t('admin.timelogsSub'),
      color: '#9DE6E2',
      fg: '#0F6D69',
      path: '/admin/marcajes',
      icon: (
        <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
          <polyline points="9 15 11 17 16 12" />
        </svg>
      ),
    },
    {
      id: 'config',
      label: t('admin.configLabel'),
      sub: t('admin.configSub'),
      color: '#FFC98A',
      fg: '#8A4B0E',
      path: '/admin/configuracion',
      icon: (
        <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
    },
  ];

  const handleLogout = () => {
    auth.logout();
    void navigate('/', { replace: true });
  };

  return (
    <div style={{ flex: 1, background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px 30px 26px' }}>
      <div className="mi-anim-fade-in" style={{ flex: 1, width: '100%', maxWidth: 645, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Header row */}
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <BackButton T={T} onClick={() => { void navigate('/'); }} />
          <button
            onClick={handleLogout}
            title="Cerrar sesión"
            style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${C.danger}30`, background: `${C.danger}10`, color: C.danger, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = `${C.danger}18`; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = `${C.danger}10`; }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>

        {/* Icon + title */}
        <div style={{ width: '100%', textAlign: 'center', marginBottom: 36 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${C.blue}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2.2" strokeLinecap="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.clockText, marginBottom: 4 }}>{t('admin.dashboardTitle')}</div>
          <div style={{ fontSize: 11, color: T.textMuted }}>{t('admin.dashboardSubtitle')}</div>
        </div>

        {/* Navigation cards */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {OPTS.map((o) => (
            <button
              key={o.id}
              onClick={() => { void navigate(o.path); }}
              style={{ width: '100%', border: 'none', borderRadius: 14, padding: '18px 22px', cursor: 'pointer', fontFamily: 'inherit', background: o.color, color: o.fg, display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left', transition: 'transform .18s, box-shadow .18s', boxShadow: `0 6px 20px ${o.color}66` }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 12px 28px ${o.color}99`; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 6px 20px ${o.color}66`; }}
            >
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: o.fg }}>
                {o.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{o.label}</div>
                <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2 }}>{o.sub}</div>
              </div>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
